/**
 * Portal text overrides (Portal text CMS, docs/specs/portal-text-cms.md).
 *
 * Staff can replace the wording of a catalog string without a deploy. This module is pure (no React, no fetch):
 *  - the rules that decide which keys may be overridden and which values are acceptable (shared by the API, which
 *    enforces them, and by the staff screen, which only mirrors them for fast feedback);
 *  - the in-memory overlay the translators consult first (setTextOverrides / clearTextOverrides).
 *
 * Lookup order everywhere: override, shipped value of the locale, English shipped value, the key itself.
 * An override is ignored (defence in depth) when its key is protected, its text is not plain text, or its {placeholder}
 * set differs from the shipped string it replaces.
 */
import type { Locale } from './index';

/** Longest override. Mirrors UI_TEXT_MAX_LENGTH in @qm/contracts/cms-text (the API test asserts they match). */
export const MAX_OVERRIDE_LENGTH = 2000;

// ------------------------------------------------------------------------------------------------ protected keys
/**
 * Keys that can NEVER be overridden from the console: legal and consent wording, privacy, security and sign-in,
 * billing and payment, error messages, the staff console's own navigation and CMS screens (so staff cannot lock
 * themselves out), and customer-facing notice/email copy. Matched against the whole key, case-insensitively.
 * Conservative on purpose: an editor who needs one of these changed asks for a deploy.
 */
export const PROTECTED_KEY_PREFIXES: readonly string[] = [
  // legal, consent, privacy
  'legal.', 'privacy.', 'consent.', 'terms.', 'commerce.notices.',
  // security, sign-in, sessions
  'security.', 'auth.', 'identity.', 'signin.', 'sign-in.', 'shell.auth.', 'shell.signin.', 'shell.signup.', 'shell.idle.',
  'shell.passkeys.', 'portal.admin.signin.', 'portal.partners.signin.', 'portal.partners.locked.',
  // money
  'billing.', 'commerce.', 'partner-commissions.', 'payment.', 'checkout.', 'portal.admin.billing.', 'portal.partners.billing.', 'portal.partners.pricing.',
  // generic error and permission messages
  'portal.kit.', 'tenancy.', 'flags.',
  // the staff console: navigation, role names, sign-in, and every CMS screen string (the Portal text screen included)
  'portal.admin.nav.', 'portal.admin.cms.', 'portal.admin.role.', 'portal.admin.staff.', 'portal.admin.flags.', 'portal.admin.impersonation.',
  // email, document and report copy (outside the portal; not covered by the CMS)
  'email-marketing.', 'growth-audit.', 'quotes.doc.', 'reports.pdf.', 'audit-quality.',
  // integration credentials
  'mcp.keys.', 'mcp.scopes.',
];

/**
 * Any dot-separated segment of a key that marks it as sensitive wherever it appears
 * (for example whatsapp-campaigns.consent.optIn, crm.invoices.title, partners.errors.notFound).
 */
export const PROTECTED_KEY_SEGMENTS: readonly string[] = [
  'error', 'errors', 'consent', 'legal', 'privacy', 'security', 'billing', 'payment', 'payments', 'checkout', 'auth', 'signin', 'signup', 'signout',
  'password', 'passkeys', 'twofa', 'mfa', 'otp', 'retention', 'deletion', 'subprocessors', 'terms', 'unsubscribe', 'refund', 'invoice', 'invoices',
  'dunning', 'wholesale', 'payouts', 'commissions', 'session', 'sessions', 'killswitch',
];
const SEGMENT_SET = new Set(PROTECTED_KEY_SEGMENTS);

/** The same wording that the legal footer carries (LEGAL_FOOTER in @qm/brand): a catalog string quoting it is not editable. */
export const PROTECTED_VALUE_NEEDLES: readonly string[] = ['owned and managed by', 'quixohub.com'];

export function isProtectedKey(key: string): boolean {
  const k = key.toLowerCase();
  if (PROTECTED_KEY_PREFIXES.some((p) => k.startsWith(p))) return true;
  return k.split('.').some((s) => SEGMENT_SET.has(s));
}

/** A shipped string that quotes the legal footer must not be reworded. */
export function quotesProtectedWording(shipped: string): boolean {
  const s = shipped.toLowerCase();
  return PROTECTED_VALUE_NEEDLES.some((n) => s.includes(n));
}

// ------------------------------------------------------------------------------------------------ value rules
/** Sorted, de-duplicated {placeholder} names (the same pattern the translators interpolate). */
export function placeholdersOf(text: string): string[] {
  return [...new Set([...text.matchAll(/\{(\w+)\}/g)].map((m) => m[1]!))].sort();
}

export type OverrideProblem = 'empty' | 'too_long' | 'markup' | 'control_chars' | 'bidi_controls' | 'placeholder_mismatch';

export interface OverrideCheck { reason: OverrideProblem; missing?: string[]; extra?: string[] }

// C0 and C1 controls (including tab, CR, LF), U+2028/2029, noncharacters U+FFFE/FFFF
// eslint-disable-next-line no-control-regex -- matching control characters is the point
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F\u2028\u2029\uFFFE\uFFFF]/;
// LRE RLE PDF LRO RLO (U+202A-202E) and LRI RLI FSI PDI (U+2066-2069): the characters used for text-direction spoofing
const BIDI_OVERRIDES = /[\u202A-\u202E\u2066-\u2069]/;

/** Plain-text rules for an override value, independent of any key. Returns null when the text is acceptable. */
export function checkOverrideText(value: string): OverrideCheck | null {
  if (value.trim().length === 0) return { reason: 'empty' };
  if (value.length > MAX_OVERRIDE_LENGTH) return { reason: 'too_long' };
  if (/[<>]/.test(value)) return { reason: 'markup' };
  if (CONTROL_CHARS.test(value)) return { reason: 'control_chars' };
  if (BIDI_OVERRIDES.test(value)) return { reason: 'bidi_controls' };
  return null;
}

/**
 * Full check of an override against the shipped string it replaces: plain text, then an EXACT placeholder match
 * (nothing removed, nothing added). Returns null when acceptable.
 */
export function checkOverride(shipped: string, value: string): OverrideCheck | null {
  const text = checkOverrideText(value);
  if (text) return text;
  const want = placeholdersOf(shipped);
  const have = placeholdersOf(value);
  const missing = want.filter((p) => !have.includes(p));
  const extra = have.filter((p) => !want.includes(p));
  return missing.length || extra.length ? { reason: 'placeholder_mismatch', missing, extra } : null;
}

// ------------------------------------------------------------------------------------------------ runtime overlay
const overlay: Record<Locale, Map<string, string>> = { en: new Map(), ar: new Map() };
let version = 0;
const listeners = new Set<() => void>();
const notify = () => { version += 1; for (const l of [...listeners]) l(); };

/**
 * Replace the overrides of one locale. Items for protected keys or with non-plain text are dropped here; the
 * placeholder check needs the shipped string, so it happens when the translator looks the key up.
 * Returns how many items were accepted.
 */
export function setTextOverrides(locale: Locale, items: Readonly<Record<string, string>>): number {
  const next = new Map<string, string>();
  for (const [key, value] of Object.entries(items)) {
    if (typeof value !== 'string' || isProtectedKey(key) || checkOverrideText(value)) continue;
    next.set(key, value);
  }
  overlay[locale] = next;
  notify();
  return next.size;
}

/** Forget the overrides of one locale (or all). The shipped text is used again. */
export function clearTextOverrides(locale?: Locale): void {
  if (locale) overlay[locale] = new Map();
  else { overlay.en = new Map(); overlay.ar = new Map(); }
  notify();
}

export function getTextOverrides(locale: Locale): Readonly<Record<string, string>> {
  return Object.fromEntries(overlay[locale]);
}

/** Increases on every change; lets a UI layer (useSyncExternalStore) re-render when overrides are applied. */
export const getTextOverridesVersion = (): number => version;
export function subscribeTextOverrides(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * The text to use for a key: the override when one exists and is safe for this shipped string, otherwise the shipped
 * string. `shipped` undefined (the key is not in this catalog) never takes an override.
 */
export function applyOverride(locale: Locale, key: string, shipped: string | undefined): string | undefined {
  if (shipped === undefined) return shipped;
  const o = overlay[locale].get(key);
  if (o === undefined) return shipped;
  const want = placeholdersOf(shipped);
  const have = placeholdersOf(o);
  return want.length === have.length && want.every((p, i) => p === have[i]) ? o : shipped;
}
